/**
 * Tiny synthesised sound engine - no audio files needed.
 * Everything is built from oscillators and noise buffers at runtime.
 */

export type SoundName =
  | 'swing'
  | 'shoot'
  | 'hit'
  | 'crit'
  | 'hurt'
  | 'kill'
  | 'levelup'
  | 'death'
  | 'spawn'
  | 'chat'
  | 'ui';

export interface SoundOptions {
  /** -1 = far left, 1 = far right. */
  pan?: number;
  /** 0..1 multiplier on top of the per-sound base volume. */
  volume?: number;
  /** Random pitch variation, default 0.08. */
  detune?: number;
}

class SoundEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  enabled = true;

  /** Must be called from a user gesture before any sound will play. */
  unlock(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return;
    }
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    this.ctx = new Ctor();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.5;
    this.master.connect(this.ctx.destination);

    // One second of white noise, reused for every percussive sound.
    const length = Math.floor(this.ctx.sampleRate * 0.5);
    const buffer = this.ctx.createBuffer(1, length, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
    this.noise = buffer;
  }

  toggle(): boolean {
    this.enabled = !this.enabled;
    return this.enabled;
  }

  play(name: SoundName, options: SoundOptions = {}): void {
    if (!this.enabled) return;
    this.unlock();
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master || ctx.state !== 'running') return;

    const detune = options.detune ?? 0.08;
    const shift = 1 + (Math.random() * 2 - 1) * detune;
    const pan = options.pan ?? 0;
    const volume = options.volume ?? 1;

    const out = ctx.createGain();
    out.gain.value = volume;
    if (pan !== 0 && ctx.createStereoPanner) {
      const panner = ctx.createStereoPanner();
      panner.pan.value = Math.max(-1, Math.min(1, pan));
      out.connect(panner);
      panner.connect(master);
    } else {
      out.connect(master);
    }

    switch (name) {
      case 'swing':
        this.noiseBurst(out, 0.13, 900 * shift, 260 * shift, 0.35);
        break;
      case 'shoot':
        this.tone(out, 'sawtooth', 620 * shift, 170 * shift, 0.16, 0.22);
        break;
      case 'hit':
        this.tone(out, 'square', 190 * shift, 90 * shift, 0.09, 0.3);
        this.noiseBurst(out, 0.07, 2400, 700, 0.22);
        break;
      case 'crit':
        this.tone(out, 'square', 300 * shift, 120, 0.13, 0.34);
        this.noiseBurst(out, 0.1, 3200, 600, 0.3);
        break;
      case 'hurt':
        this.tone(out, 'triangle', 220 * shift, 70, 0.22, 0.4);
        break;
      case 'kill':
        this.tone(out, 'triangle', 160 * shift, 480, 0.24, 0.3);
        this.noiseBurst(out, 0.16, 1600, 200, 0.2);
        break;
      case 'levelup':
        this.arpeggio(out, [523.25, 659.25, 783.99, 1046.5], 0.13, 0.26);
        break;
      case 'death':
        this.tone(out, 'sawtooth', 320, 55, 0.9, 0.3);
        break;
      case 'spawn':
        this.tone(out, 'sine', 900 * shift, 1500, 0.1, 0.1);
        break;
      case 'chat':
        this.tone(out, 'sine', 880, 1180, 0.07, 0.16);
        break;
      case 'ui':
        this.tone(out, 'sine', 660, 880, 0.06, 0.14);
        break;
    }
  }

  private tone(
    out: GainNode,
    type: OscillatorType,
    from: number,
    to: number,
    duration: number,
    gain: number,
    delay = 0,
  ): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const t = ctx.currentTime + delay;
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(Math.max(20, from), t);
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, to), t + duration);

    const env = ctx.createGain();
    env.gain.setValueAtTime(0.0001, t);
    env.gain.exponentialRampToValueAtTime(gain, t + 0.008);
    env.gain.exponentialRampToValueAtTime(0.0001, t + duration);

    osc.connect(env);
    env.connect(out);
    osc.start(t);
    osc.stop(t + duration + 0.02);
  }

  private noiseBurst(
    out: GainNode,
    duration: number,
    fromFreq: number,
    toFreq: number,
    gain: number,
  ): void {
    const ctx = this.ctx;
    if (!ctx || !this.noise) return;
    const t = ctx.currentTime;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;

    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.Q.value = 1.1;
    filter.frequency.setValueAtTime(fromFreq, t);
    filter.frequency.exponentialRampToValueAtTime(Math.max(60, toFreq), t + duration);

    const env = ctx.createGain();
    env.gain.setValueAtTime(gain, t);
    env.gain.exponentialRampToValueAtTime(0.0001, t + duration);

    src.connect(filter);
    filter.connect(env);
    env.connect(out);
    src.start(t);
    src.stop(t + duration + 0.02);
  }

  private arpeggio(out: GainNode, notes: number[], step: number, gain: number): void {
    notes.forEach((freq, i) => {
      this.tone(out, 'triangle', freq, freq, step * 2.2, gain, i * step);
    });
  }
}

export const sfx = new SoundEngine();
